import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Mic, Paperclip, Send, Loader2, Sparkles, ChevronDown, CheckCircle, AlertCircle, Clock, Briefcase, FileText, X } from "lucide-react";
import Layout from "../../components/layout/Layout";
import { useAuth } from "../../context/AuthContext";
import TaskDraftCard from "../../components/ui/TaskDraftCard";
import TaskCard from "../../components/ui/TaskCard";
import TaskDrawer from "../../components/ui/TaskDrawer";
import DocumentAnalyzer from "../../components/ui/DocumentAnalyzer";

const RAW_PROMPT_KEY = "obstuchim_raw_prompt";
const RAW_CONTEXT_KEY = "obstuchim_raw_context";
const ACTIVE_SUMMARY_KEY = "obstuchim_active_summary";

export default function AiWorkspace() {
  const { profile } = useAuth();
  const [taskText, setTaskText] = useState(() => localStorage.getItem(RAW_PROMPT_KEY) || "");
  const [extraContext, setExtraContext] = useState(() => localStorage.getItem(RAW_CONTEXT_KEY) || "");
  const [showBriefPanel, setShowBriefPanel] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isAiProcessing, setIsAiProcessing] = useState(false);
  const [aiSummary, setAiSummary] = useState(() => localStorage.getItem(ACTIVE_SUMMARY_KEY) || null);
  const [localSaveStatus, setLocalSaveStatus] = useState("saved"); // 'saving', 'saved'
  const textTimeoutRef = useRef(null);
  
  // Дашборд
  const [drafts, setDrafts] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [selectedDraftId, setSelectedDraftId] = useState(null);

  // Sync text to local storage to prevent data loss on refresh
  useEffect(() => {
    setLocalSaveStatus("saving");
    if (textTimeoutRef.current) clearTimeout(textTimeoutRef.current);
    
    textTimeoutRef.current = setTimeout(() => {
      localStorage.setItem(RAW_PROMPT_KEY, taskText);
      localStorage.setItem(RAW_CONTEXT_KEY, extraContext);
      setLocalSaveStatus("saved");
    }, 500);
    
    return () => clearTimeout(textTimeoutRef.current);
  }, [taskText, extraContext]);

  // Fetch drafts and tasks
  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem("obstuchim_token");
      const [draftsRes, tasksRes] = await Promise.all([
        fetch("/api/drafts", { headers: { "Authorization": `Bearer ${token}` } }),
        fetch("/api/tasks", { headers: { "Authorization": `Bearer ${token}` } })
      ]);
      
      if (draftsRes.ok) {
        const draftsData = await draftsRes.json();
        setDrafts(draftsData);
      }
      if (tasksRes.ok) {
        const tasksData = await tasksRes.json();
        setTasks(tasksData.filter(t => t.status !== 'DONE' && t.status !== 'CANCELLED'));
      }
    } catch (err) {
      console.error("Failed to load dashboard data", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Заглушка для микрофона
  const handleMicClick = () => {
    setIsRecording(!isRecording);
    if (!isRecording) {
      setTimeout(() => {
        setIsRecording(false);
        setTaskText(prev => prev + (prev ? " " : "") + "Надо подготовить новый договор...");
      }, 2000);
    }
  };

  const handleSendTask = async (e) => {
    e.preventDefault();
    if (!taskText.trim()) return;
    
    const savedGoal = taskText.trim();
    setIsAiProcessing(true);
    setAiSummary(null); // Сбросим старое саммари перед новой генерацией
    localStorage.removeItem(ACTIVE_SUMMARY_KEY);
    
    try {
      const token = localStorage.getItem("obstuchim_token");
      const res = await fetch("/api/ai/copilot/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ 
          message: savedGoal,
          extraContext: extraContext.trim() || undefined
        })
      });
      
      if (res.ok) {
        await fetchData();
        localStorage.setItem(ACTIVE_SUMMARY_KEY, savedGoal);
        setAiSummary(savedGoal);
        setTaskText("");
        setExtraContext("");
        setShowBriefPanel(false);
        localStorage.removeItem(RAW_PROMPT_KEY);
        localStorage.removeItem(RAW_CONTEXT_KEY);
      }
    } catch (err) {
      console.error("Task processing error", err);
    } finally {
      setIsAiProcessing(false);
    }
  };

  const handleApproveDraft = async (draftId) => {
    try {
      const token = localStorage.getItem("obstuchim_token");
      const res = await fetch(`/api/drafts/${draftId}/approve`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        if (selectedDraftId === draftId) {
          setSelectedDraftId(null);
        }
        await fetchData();
      }
    } catch (err) {
      console.error("Failed to approve draft", err);
    }
  };

  const handleDeleteDraft = async (draftId) => {
    try {
      const token = localStorage.getItem("obstuchim_token");
      const res = await fetch(`/api/drafts/${draftId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        setDrafts(prev => prev.filter(d => d.id !== draftId));
        if (selectedDraftId === draftId) {
          setSelectedDraftId(null);
        }
      }
    } catch (err) {
      console.error("Failed to delete draft", err);
    }
  };

  const handleUpdateDraftOptimistic = (draftId, updatedDraft) => {
    setDrafts(prev => prev.map(d => {
      if (d.id === draftId) {
        return {
          ...d,
          ...updatedDraft,
          payloadJson: updatedDraft.payloadJson || {
            ...(d.payloadJson || {}),
            ...updatedDraft
          }
        };
      }
      return d;
    }));
  };

  const handleDeleteTask = async (taskId) => {
    try {
      const token = localStorage.getItem("obstuchim_token");
      const res = await fetch(`/api/tasks/${taskId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        setTasks(prev => prev.filter(t => t.id !== taskId));
        if (selectedTaskId === taskId) {
          setSelectedTaskId(null);
        }
      }
    } catch (err) {
      console.error("Failed to delete task", err);
    }
  };

  const handleUpdateTaskOptimistic = (taskId, updates) => {
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, ...updates } : t));
  };

  const tasksInProgress = tasks.filter(t => t.status === 'IN_PROGRESS' || t.status === 'TODO');
  const tasksInReview = tasks.filter(t => t.status === 'IN_REVIEW' || t.status === 'REVIEW');

  return (
    <Layout>
      <div className="min-h-screen pt-20 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto flex flex-col gap-8">
        
        {/* Хедер & Переключатель контекста */}
        <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="text-neutral-500 font-extralight text-sm mb-1">Рабочее пространство</p>
            <div className="flex items-center gap-2 cursor-pointer group">
              <div className="w-8 h-8 rounded-md bg-neutral-800 border border-neutral-700 flex items-center justify-center">
                <Briefcase className="w-4 h-4 text-primary-400" />
              </div>
              <h1 className="text-2xl font-light text-neutral-100 group-hover:text-primary-400 transition-colors">
                {profile?.company_name || "Мой первый проект"}
              </h1>
              <ChevronDown className="w-5 h-5 text-neutral-600 group-hover:text-primary-400 transition-colors" />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button className="px-4 py-2 rounded-md bg-neutral-900 border border-neutral-800 text-neutral-300 hover:text-white font-extralight text-sm transition-colors flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-accent-500" />
              Призвать Эксперта
            </button>
          </div>
        </header>

        {/* AI-Анализатор Документов */}
        <DocumentAnalyzer 
          onGenerateTasks={(conclusions) => {
            setTaskText(conclusions);
            // Simulate direct send after brief timeout
            setTimeout(() => {
              // Note: We'd normally call handleSendTask but since it expects an event,
              // we can just wait for user to click send, or we trigger it.
              // For now, filling the context is enough for the MVP flow.
            }, 100);
          }} 
        />

        {/* Command Center - Умная Строка */}
        <div className="bg-neutral-900/40 border border-neutral-800 rounded-xl p-4 sm:p-6 backdrop-blur-sm shadow-xl relative z-20">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-extralight text-neutral-400">Декомпозиция новой задачи</span>
            <div className="h-4 flex items-center">
              <AnimatePresence mode="wait">
                {localSaveStatus === 'saving' && (
                  <motion.div
                    key="saving"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center gap-1.5 text-neutral-500 text-xs font-extralight"
                  >
                    <Loader2 className="w-3 h-3 animate-spin" />
                    <span>Сохранение...</span>
                  </motion.div>
                )}
                {localSaveStatus === 'saved' && taskText.length > 0 && (
                  <motion.div
                    key="saved"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center gap-1.5 text-neutral-500 text-xs font-extralight"
                  >
                    <CheckCircle className="w-3 h-3 text-green-500/70" />
                    <span>Сохранено локально</span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
          <form onSubmit={handleSendTask} className="relative">
            <AnimatePresence>
              {showBriefPanel && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden mb-2"
                >
                  <div className="bg-neutral-950/80 border border-neutral-800 rounded-lg p-3 sm:p-4 mb-2 relative">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-extralight text-neutral-400 flex items-center gap-1.5 uppercase tracking-wider">
                        <FileText className="w-3.5 h-3.5 text-accent-500" />
                        Бриф / Текст документа (Доп. контекст)
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowBriefPanel(false)}
                        className="text-neutral-500 hover:text-neutral-300 transition-colors p-1"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <textarea
                      value={extraContext}
                      onChange={(e) => setExtraContext(e.target.value)}
                      placeholder="Вставьте сюда текст брифа, переписку, условия договора или любые вводные данные..."
                      rows={4}
                      className="w-full bg-transparent border-none text-sm font-extralight text-neutral-300 focus:outline-none focus:ring-0 p-0 placeholder:text-neutral-600 resize-none overflow-y-auto"
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <textarea
              value={taskText}
              onChange={(e) => setTaskText(e.target.value)}
              placeholder="Опишите инициативу, проблему или прикрепите документ..."
              rows={taskText.split('\n').length > 1 ? Math.min(taskText.split('\n').length, 5) : 1}
              className="w-full bg-neutral-950/50 border border-neutral-800 text-neutral-100 placeholder:text-neutral-600 rounded-lg pl-4 pr-32 py-4 font-extralight focus:outline-none focus:border-primary-700 transition-colors resize-none overflow-hidden min-h-[60px]"
              style={{ height: 'auto' }}
            />
            
            {/* Опции ввода (Скрепка, Микрофон, Отправить) */}
            <div className="absolute right-2 bottom-2 flex items-center gap-1">
              <button 
                type="button"
                onClick={() => setShowBriefPanel(!showBriefPanel)}
                className={`w-10 h-10 flex items-center justify-center rounded-md transition-colors relative ${showBriefPanel || extraContext.length > 0 ? 'text-accent-500 bg-accent-900/20' : 'text-neutral-500 hover:text-neutral-300 hover:bg-neutral-800'}`}
                title="Прикрепить бриф или контекст"
              >
                <Paperclip className="w-5 h-5" />
                {extraContext.length > 0 && !showBriefPanel && (
                  <span className="absolute top-2 right-2 w-2 h-2 bg-accent-500 rounded-full" />
                )}
              </button>
              <button 
                type="button"
                onClick={handleMicClick}
                className={`w-10 h-10 flex items-center justify-center rounded-md transition-colors relative ${isRecording ? 'text-red-500' : 'text-neutral-500 hover:text-neutral-300 hover:bg-neutral-800'}`}
              >
                <Mic className="w-5 h-5" />
                {isRecording && (
                  <span className="absolute inset-0 rounded-md border border-red-500/50 animate-ping" />
                )}
              </button>
              <div className="w-px h-6 bg-neutral-800 mx-1" />
              <button 
                type="submit"
                disabled={!taskText.trim() || isAiProcessing}
                className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-700 hover:bg-primary-600 text-white disabled:opacity-50 transition-colors"
              >
                {isAiProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 ml-0.5" />}
              </button>
            </div>
          </form>

          {/* Индикатор автосохранения / процесса */}
          <AnimatePresence>
            {isAiProcessing && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="pt-4 flex items-center gap-2 text-primary-400 text-sm font-extralight overflow-hidden"
              >
                <Sparkles className="w-4 h-4 animate-pulse" />
                <span>ИИ-Помощник декомпозирует вашу инициативу...</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Мост понимания (AI Summary) */}
        <AnimatePresence>
          {aiSummary && drafts.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10, height: 0 }}
              animate={{ opacity: 1, y: 0, height: "auto" }}
              exit={{ opacity: 0, scale: 0.95, height: 0 }}
              className="bg-accent-900/10 border border-accent-500/30 rounded-xl p-5 sm:p-6 mb-2 overflow-hidden relative"
            >
              <div className="absolute top-0 left-0 w-1 h-full bg-accent-500" />
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-accent-400 font-medium text-sm tracking-wide uppercase">
                    <Sparkles className="w-4 h-4" />
                    Декомпозиция завершена
                  </div>
                  <p className="text-neutral-300 font-light text-base leading-relaxed">
                    Я проанализировал вашу инициативу: <span className="text-white font-normal bg-white/5 px-1.5 py-0.5 rounded">"{aiSummary}"</span>. 
                    Чтобы достичь этого результата, я разбил процесс на <strong className="text-white font-medium">{drafts.length}</strong> логических шагов.
                  </p>
                  <p className="text-neutral-400 font-extralight text-sm">
                    Изучите предложенные черновики в колонке слева. Утвердите подходящие задачи в работу или удалите лишние.
                  </p>
                </div>
                <button
                  onClick={() => {
                    localStorage.removeItem(ACTIVE_SUMMARY_KEY);
                    setAiSummary(null);
                  }}
                  className="p-1.5 text-neutral-500 hover:text-white hover:bg-white/10 rounded-md transition-colors shrink-0"
                  title="Закрыть"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Рабочая Панель (Канбан / Дашборд) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative z-10">
          
          {/* Колонка: Требует Внимания (Черновики и Срочное) */}
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b border-neutral-800 pb-2">
              <h3 className="text-neutral-400 font-light flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-accent-500" />
                Требует внимания
              </h3>
              <span className="text-xs bg-neutral-800 text-neutral-400 px-2 py-0.5 rounded-full">{drafts.length}</span>
            </div>
            
            <AnimatePresence>
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-neutral-600" />
                </div>
              ) : drafts.map(draft => (
                <TaskDraftCard
                  key={draft.id}
                  draft={draft}
                  onApprove={handleApproveDraft}
                  onDelete={handleDeleteDraft}
                  onEdit={(d) => setSelectedDraftId(d.id)}
                />
              ))}
              
              {!isLoading && drafts.length === 0 && (
                <div className="text-center py-8 text-neutral-600 font-extralight text-sm border border-dashed border-neutral-800 rounded-lg">
                  Всё чисто. Опишите новую инициативу выше.
                </div>
              )}
            </AnimatePresence>
          </div>

          {/* Колонка: В Работе */}
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b border-neutral-800 pb-2">
              <h3 className="text-neutral-400 font-light flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-400" />
                В работе
              </h3>
              <span className="text-xs bg-neutral-800 text-neutral-400 px-2 py-0.5 rounded-full">{tasksInProgress.length}</span>
            </div>
            
            <AnimatePresence>
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-neutral-600" />
                </div>
              ) : tasksInProgress.map(task => (
                <TaskCard
                  key={task.id}
                  task={task}
                  onDelete={handleDeleteTask}
                  onClick={setSelectedTaskId}
                />
              ))}

              {!isLoading && tasksInProgress.length === 0 && (
                <div className="text-center py-8 text-neutral-600 font-extralight text-sm border border-dashed border-neutral-800 rounded-lg">
                  Пока нет активных задач
                </div>
              )}
            </AnimatePresence>
          </div>

          {/* Колонка: Ждут Приемки */}
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b border-neutral-800 pb-2">
              <h3 className="text-neutral-400 font-light flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                Ждут приемки
              </h3>
              <span className="text-xs bg-neutral-800 text-neutral-400 px-2 py-0.5 rounded-full">{tasksInReview.length}</span>
            </div>
            
            <AnimatePresence>
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-neutral-600" />
                </div>
              ) : tasksInReview.map(task => (
                <TaskCard
                  key={task.id}
                  task={task}
                  onDelete={handleDeleteTask}
                  onClick={setSelectedTaskId}
                />
              ))}

              {!isLoading && tasksInReview.length === 0 && (
                <div className="text-center py-8 text-neutral-600 font-extralight text-sm border border-dashed border-neutral-800 rounded-lg">
                  Нет задач на проверке
                </div>
              )}
            </AnimatePresence>
          </div>

        </div>
      </div>
      
      {/* Drawer for Tasks */}
      <TaskDrawer
        task={tasks.find(t => t.id === selectedTaskId) || null}
        isOpen={!!selectedTaskId}
        onClose={() => setSelectedTaskId(null)}
        onUpdate={handleUpdateTaskOptimistic}
        onDelete={handleDeleteTask}
      />

      {/* Drawer for Drafts */}
      <TaskDrawer
        task={drafts.find(d => d.id === selectedDraftId) || null}
        isDraft={true}
        isOpen={!!selectedDraftId}
        onClose={() => setSelectedDraftId(null)}
        onUpdate={handleUpdateDraftOptimistic}
        onDelete={handleDeleteDraft}
        onApprove={handleApproveDraft}
      />
    </Layout>
  );
}
